import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Paper, IconButton, Tabs, Tab } from '@mui/material';
import { Terminal as TerminalIcon, Add, Close } from '@mui/icons-material';

const TerminalComponent = ({ webContainer, height = 200 }) => {
  const [terminals, setTerminals] = useState([]);
  const [activeTerminal, setActiveTerminal] = useState(0);
  const [output, setOutput] = useState(['Welcome to WebContainer Terminal', '$ ']);
  const terminalRef = useRef(null);

  // Initialize with first terminal
  useEffect(() => {
    if (terminals.length === 0) {
      addNewTerminal();
    }
  }, []);

  const addNewTerminal = () => {
    const newTerminal = {
      id: Date.now(),
      title: `Terminal ${terminals.length + 1}`,
      output: ['$ '],
      history: []
    };
    
    setTerminals(prev => [...prev, newTerminal]);
    setActiveTerminal(terminals.length);
  };

  const removeTerminal = (index) => {
    if (terminals.length > 1) {
      setTerminals(prev => prev.filter((_, i) => i !== index));
      if (activeTerminal >= index && activeTerminal > 0) {
        setActiveTerminal(activeTerminal - 1);
      }
    }
  };

  const executeCommand = async (command) => {
    const trimmedCommand = command.trim();
    if (!trimmedCommand) return;

    // Add command to output
    setOutput(prev => [...prev, `$ ${trimmedCommand}`]);

    try {
      if (webContainer && webContainer.spawn) {
        // Real WebContainer command execution
        const parts = trimmedCommand.split(' ');
        const cmd = parts[0];
        const args = parts.slice(1);
        
        const process = await webContainer.spawn(cmd, args);
        
        // Handle process output
        if (process.output) {
          const reader = process.output.getReader();
          
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              const text = new TextDecoder().decode(value);
              setOutput(prev => [...prev, text]);
            }
          } catch (error) {
            setOutput(prev => [...prev, `Error reading output: ${error.message}`]);
          }
        }
        
        await process.exit;
        setOutput(prev => [...prev, '$ ']);
      } else {
        // Mock command execution for development
        await mockCommandExecution(trimmedCommand);
      }
    } catch (error) {
      setOutput(prev => [...prev, `Error: ${error.message}`, '$ ']);
    }
  };

  const mockCommandExecution = async (command) => {
    // Simulate delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const responses = {
      'ls': 'index.js  package.json  README.md  public/',
      'pwd': '/workspace',
      'whoami': 'webcontainer',
      'node --version': 'v18.17.0',
      'npm --version': '9.6.7',
      'npm install': 'added 57 packages in 2.3s',
      'npm start': 'Server running on http://localhost:3111',
      'npm run dev': 'Development server started',
      'echo': command.replace('echo ', ''),
      'date': new Date().toString(),
      'help': 'Available commands: ls, pwd, whoami, node, npm, echo, date, clear'
    };

    const [cmd, ...args] = command.split(' ');
    let response = responses[command] || responses[cmd];
    
    if (!response) {
      if (cmd === 'echo') {
        response = args.join(' ');
      } else if (cmd === 'clear') {
        setOutput(['$ ']);
        return;
      } else {
        response = `command not found: ${cmd}`;
      }
    }

    setOutput(prev => [...prev, response, '$ ']);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      const command = event.target.value;
      event.target.value = '';
      executeCommand(command);
    }
  };

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', backgroundColor: '#1e1e1e' }}>
      {/* Terminal Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'grey.800', backgroundColor: '#2d2d2d' }}>
        <Tabs
          value={activeTerminal}
          onChange={(e, newValue) => setActiveTerminal(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              color: '#cccccc',
              fontSize: '0.75rem',
              minHeight: 36,
              textTransform: 'none'
            }
          }}
        >
          {terminals.map((terminal, index) => (
            <Tab
              key={terminal.id}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <TerminalIcon sx={{ fontSize: 16 }} />
                  <span>{terminal.title}</span>
                  {terminals.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTerminal(index);
                      }}
                      sx={{ color: '#cccccc', padding: 0.5 }}
                    >
                      <Close sx={{ fontSize: 12 }} />
                    </IconButton>
                  )}
                </Box>
              }
            />
          ))}
          <Tab
            icon={<Add />}
            onClick={addNewTerminal}
            sx={{ minWidth: 40, color: '#cccccc' }}
          />
        </Tabs>
      </Box>

      {/* Terminal Content */}
      <Box 
        ref={terminalRef}
        sx={{ 
          flexGrow: 1, 
          backgroundColor: '#1e1e1e', 
          color: '#cccccc',
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          fontSize: '0.875rem',
          padding: 1,
          overflow: 'auto'
        }}
      >
        {/* Output */}
        <Box>
          {output.map((line, index) => (
            <Typography
              key={index}
              component="div"
              sx={{
                fontFamily: 'inherit',
                fontSize: 'inherit',
                whiteSpace: 'pre-wrap',
                margin: 0,
                color: line.startsWith('$') ? '#4CAF50' : 
                       line.startsWith('Error') ? '#f44336' : 
                       '#cccccc'
              }}
            >
              {line}
            </Typography>
          ))}
        </Box>

        {/* Input */}
        <Box display="flex" alignItems="center">
          <Typography sx={{ color: '#4CAF50', fontFamily: 'inherit' }}>
            $ 
          </Typography>
          <input
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#cccccc',
              fontFamily: 'Menlo, Monaco, "Courier New", monospace',
              fontSize: '0.875rem',
              marginLeft: 8,
              flexGrow: 1
            }}
            onKeyPress={handleKeyPress}
            autoFocus
            placeholder="Type a command..."
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TerminalComponent;